import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  fieldBounds,
  fieldStep,
  formatDraftValue,
  harderSign,
  programRoundLabel,
  type DraftLineOptions,
  type EffortScale,
  type PresetField,
  type PresetId,
  type PresetValues,
} from "@heraion/shared";

import { IconButton } from "../primitives/IconButton";
import { Stepper } from "../primitives/Stepper";
import type { UiTheme } from "../theme";
import type { RuleWords } from "./ruleWords";
import type { V3Styles } from "./v3Styles";

// One field of the picked preset in the rule sheet: a stepper with its step and
// its format, or for «efforts» the cycle of values with one more / one less.

const WAVE_MIN = 2;
const WAVE_MAX = 6;

export function RuleField({
  theme,
  styles,
  field,
  presetId,
  values,
  axisScale,
  lineOptions,
  words,
  onChange,
}: {
  theme: UiTheme;
  styles: V3Styles;
  field: PresetField;
  presetId: PresetId | null;
  values: PresetValues;
  axisScale: EffortScale;
  lineOptions: DraftLineOptions;
  words: RuleWords;
  onChange: (key: string, v: number | number[]) => void;
}) {
  const { t } = useTranslation();
  const local = useMemo(() => makeStyles(theme), [theme]);
  const f = field;
  const unit = lineOptions.cellUnit;
  const label = (key: string) => t(`editorV3.rule.field.${key}`, { defaultValue: key });
  if (f.key === "efforts") {
    const waves = Array.isArray(values.efforts) ? values.efforts : [];
    const [lo, hi] = fieldBounds(f, axisScale, unit);
    return (
      <View style={local.waves}>
        <Text style={styles.name}>{label("efforts")}</Text>
        {waves.map((v, i) => (
          <Stepper
            key={i}
            theme={theme}
            label={programRoundLabel(i + 1)}
            value={v}
            min={lo}
            max={hi}
            step={fieldStep(f, unit)}
            format={(x) => formatDraftValue(axisScale, x, lineOptions)}
            onChange={(x) => onChange("efforts", waves.map((y, j) => (j === i ? x : y)))}
          />
        ))}
        <View style={local.row}>
          <IconButton
            theme={theme}
            name="minus"
            accessibilityLabel={t("editorV3.rule.waveLess", { defaultValue: "One value less in the cycle" })}
            disabled={waves.length <= WAVE_MIN}
            onPress={() => onChange("efforts", waves.slice(0, -1))}
          />
          <IconButton
            theme={theme}
            name="plus"
            accessibilityLabel={t("editorV3.rule.waveMore", { defaultValue: "One value more in the cycle" })}
            disabled={waves.length >= WAVE_MAX}
            onPress={() => onChange("efforts", [...waves, waves[waves.length - 1] ?? 7])}
          />
        </View>
      </View>
    );
  }
  const offWord = t("editorV3.rule.field.off", { defaultValue: "None" });
  const fmt = (v: number) => {
    if (f.optional && v === 0) return offWord;
    // A step carries its sign: «+2.5 kg», «−2».
    const sign = f.delta ? (v > 0 ? "+" : v < 0 ? "−" : "") : "";
    const a = f.delta ? Math.abs(v) : v;
    // An effort step on its axis with its true sign, as the sentence says it:
    // «RPE +1», «RIR −1» (the field counts «harder»).
    if (f.unit === "effort") return f.delta ? words.step(axisScale, v * harderSign(axisScale)) : formatDraftValue(axisScale, a, lineOptions);
    if (f.unit === "load") return `${sign}${formatDraftValue("absolute", a, lineOptions)}`;
    if (f.unit === "percent") return `${a}%`;
    if (f.unit === "seconds") return `${sign}${a} ${lineOptions.labels.seconds}`;
    return `${sign}${a}`;
  };
  const [lo, hi] = fieldBounds(f, axisScale, unit, presetId ? { id: presetId, values } : undefined);
  const cur = typeof values[f.key] === "number" ? (values[f.key] as number) : 0;
  return (
    <Stepper
      theme={theme}
      label={label(f.key)}
      value={cur}
      // A rule read off the row may sit outside the preset's range (a ramp
      // that goes down): the stepper starts from it, its sign kept.
      min={Math.min(lo, cur)}
      max={Math.max(hi, cur)}
      step={fieldStep(f, unit)}
      format={fmt}
      onChange={(v) => onChange(f.key, v)}
    />
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    waves: { gap: s.s2 },
    // s4: the two 36 boxes' hitSlops (8 each side) do not overlap.
    row: { flexDirection: "row", gap: s.s4 },
  });
}
